import { useFormik } from 'formik'
import queryString from 'query-string'
import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { toast } from 'react-toastify'
import styled from 'styled-components'
import { Form } from 'sutra/components/Form'
import * as Yup from 'yup'

import { handleException } from 'clients/exception-handling'
import { Loader } from 'components'
import { useAuthContext } from 'features/auth'
import { Button } from 'features/auth/components/auth-button'
import { AuthWrapper } from 'features/auth/components/AuthWrapper'
import { FormItem } from 'features/auth/components/FormItem'
import { InputPassword } from 'features/auth/components/Input'
import { ServerValidationError } from 'features/auth/components/ServerValidationError'
import { isAuthErrorNamed } from 'features/auth/expected-auth-error'
import { SIGN_IN } from 'features/auth/paths'
import type { User } from 'features/auth/types/auth'
import { resetPassword, signIn } from 'features/auth/utils'

const NewPasswordSchema = Yup.object().shape({
  password: Yup.string()
    .required('Required')
    .min(8, 'Password must be at least 8 characters in length')
    .test(
      'isValidPass',
      'Password does not meet complexity requirements',
      (value) => {
        const hasUpperCase = /[A-Z]/.test(value)
        const hasLowerCase = /[a-z]/.test(value)
        const hasNumber = /[0-9]/.test(value)
        const hasSymbol = /[^a-zA-Z\d\s:]/.test(value)
        let validConditions = 0
        const numberOfMustBeValidConditions = 4
        const conditions = [hasLowerCase, hasUpperCase, hasNumber, hasSymbol]
        conditions.forEach((condition) =>
          condition ? validConditions++ : null,
        )
        if (validConditions >= numberOfMustBeValidConditions) {
          return true
        }
        return false
      },
    ),
  confirm_password: Yup.string()
    .required('Required')
    .oneOf([Yup.ref('password')], 'Passwords do not match'),
  code: Yup.string().required('Required'),
  email: Yup.string().email().required('Required'),
})

export const NewPasswordForm = (): JSX.Element => {
  const authContext = useAuthContext()
  const [serverValidationError, setServerValidationError] = useState('')
  const navigate = useNavigate()
  const location = useLocation()
  const formik = useFormik({
    initialValues: { code: '', email: '', password: '', confirm_password: '' },
    validationSchema: NewPasswordSchema,
    onSubmit: async ({ code, password, email }) => {
      try {
        await resetPassword(email, code, password)
        const user = await signIn(email, password)

        await authContext.signIn(user as unknown as User)
        toast.info('Success')
        navigate(SIGN_IN)
      } catch (e) {
        handleException(e, {
          operation: 'auth.password.reset',
          disposition: 'expected',
          isExpectedError: (cause) =>
            isAuthErrorNamed(
              cause,
              'CodeMismatchException',
              'ExpiredCodeException',
              'InvalidPasswordException',
              'NotAuthorizedException',
              'LimitExceededException',
            ),
          reason: 'The identity provider rejected the password or reset code.',
        })
        setServerValidationError((e as Error).message)
      }
    },
  })
  const passwordError = formik.touched.password && formik.errors.password
  const confirmPasswordError =
    formik.touched.confirm_password && formik.errors.confirm_password
  const queryParams = queryString.parse(location.search)
  const { setFieldValue } = formik
  const queryCode = queryParams['code']
  const queryData = queryParams['data']

  useEffect(() => {
    if (queryData && queryCode) {
      const data = JSON.parse(atob(queryData as string))

      setFieldValue('code', queryCode as string)
      setFieldValue('email', data.email as string)
    } else {
      navigate(SIGN_IN)
    }
  }, [setFieldValue, navigate, queryCode, queryData])

  const onChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setServerValidationError('')
    formik.handleChange(e)
  }

  return (
    <AuthWrapper>
      <Wrapper>
        <StyledForm onFinish={formik.submitForm} layout="vertical">
          <FormTitle>Enter New Password</FormTitle>
          <StyledFormItem
            label="New password"
            validateStatus={passwordError ? 'error' : undefined}
            help={passwordError}
          >
            <InputPassword
              name="password"
              error={!!passwordError}
              placeholder="*******"
              onChange={onChange}
              value={formik.values.password}
            />
          </StyledFormItem>
          <StyledFormItem
            label="Repeat Password"
            validateStatus={confirmPasswordError ? 'error' : undefined}
            help={confirmPasswordError}
            style={{ marginBottom: '9px' }}
          >
            <InputPassword
              name="confirm_password"
              error={!!confirmPasswordError}
              placeholder="*******"
              onChange={onChange}
              value={formik.values.confirm_password}
            />
          </StyledFormItem>
          <HelpMessage isError={passwordError}>
            Password must:
            <ul>
              <li>
                Be at least <b>8 characters</b> in length
              </li>
              <li>
                Include at least <b>one of each</b> of the following:
                <ul>
                  <li>Capital Letter</li>
                  <li>Lowercase Letter</li>
                  <li>Number</li>
                  <li>Special Character</li>
                </ul>
              </li>
            </ul>
          </HelpMessage>
          {serverValidationError && (
            <ServerValidationError message={serverValidationError} />
          )}

          <StyledButton variant="primary" type="submit">
            Reset & Login
          </StyledButton>
        </StyledForm>
        {formik.isSubmitting && (
          <Overlay>
            <Loader />
          </Overlay>
        )}
      </Wrapper>
    </AuthWrapper>
  )
}

export default NewPasswordForm

const Wrapper = styled.div`
  display: flex;
  flex-flow: column nowrap;
  width: 100%;
  color: var(--color-auth-grey);
  background: #fff;
`

const StyledForm = styled(Form)`
  position: relative;
  max-width: 410px;
  width: 100%;
  margin: 0 auto;
  padding: 35px 45px;

  color: var(--color-auth-grey);
  border: 0.5px solid rgba(156, 168, 187, 0.5);
  border-radius: 6px;

  @media (max-width: 768px) {
    padding: 35px;
    border: none;
  }
`

const StyledFormItem = styled(FormItem)`
  margin-bottom: 20px;
`

const FormTitle = styled.div`
  margin-bottom: 38px;

  text-align: center;
  font-weight: 500;
  font-size: 20px;
  line-height: 27px;
`

const HelpMessage = styled.div<{ isError?: string | false }>`
  margin-bottom: 10px;

  color: ${(props) => (props.isError ? '#ff0033;' : 'inherit')};
  font-weight: 300;
  font-size: 12px;
  line-height: 16px;
`

const StyledButton = styled(Button)`
  margin-top: 30px;
`

const Overlay = styled.div`
  background: #fff;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: center;
`
